import { createStore, createEvent, Event, createEffect, combine } from 'effector';
import { get } from 'lodash';
import IAuthUser from '@interfaces/IAuthUser';
import storage from '@helpers/localStorage';
import createPreloadersStore from './preloaders';
import request from '@helpers/request';
import api from '@constants/endpoints';
import qs from 'qs';
import BASIC_AUTH_TOKEN from '../constants/basicAuthToken';
import { message } from 'antd';
import { resetTrackers, addVisibilityListeners, initUser } from '@helpers/timeTracker';

type IUserState = IAuthUser | null;

interface ILoginCreds {
  username: string;
  password: string;
}

interface ITokenCreds extends ILoginCreds {
  totp: string;
}

interface ICheckTotpResponse {
  setupNeeded: boolean;
}

interface ISetupTotpResponse {
  qrCode: string;
}

export const saveUser: Event<IAuthUser> = createEvent('Save User');
export const logout = createEvent('Log Out');

export const saveTokens: Event<Record<string, string>> = createEvent('Save Tokens');

export const checkTotp = createEffect({
  name: 'checkTotp',
  async handler(creds: ILoginCreds) {
    const Authorization = BASIC_AUTH_TOKEN;

    const response = await request(api.auth.checkTotp, {
      headers: { Authorization },
      data: creds,
    });

    return response.data as ICheckTotpResponse;
  },
});

export const setupTotp = createEffect({
  name: 'setupTotp',
  async handler(creds: ILoginCreds) {
    const Authorization = BASIC_AUTH_TOKEN;

    const response = await request(api.auth.setupTotp, {
      headers: { Authorization },
      data: creds,
    });

    return response.data as ISetupTotpResponse;
  },
});

export const getToken = createEffect({
  name: 'getToken',
  async handler(creds: ITokenCreds) {
    const Authorization = BASIC_AUTH_TOKEN;
    const data = qs.stringify({
      grant_type: 'admin_password',
      auth_type: 'email',
      username: creds.username,
      password: creds.password,
      totp: creds.totp,
    });

    const response = await request(api.auth.getToken, {
      headers: { Authorization },
      data,
    });

    return response;
  },
});

export const getCurrentAccount = createEffect({
  name: 'getCurrentAccount',
  async handler() {
    const response = await request(api.auth.getCurrentAccount);
    const userId = get(response, ['data', 'id']);
    initUser(userId);
    addVisibilityListeners();
    return response;
  },
});

getToken.done.watch((data) => {
  storage.writeObject('tokens', data.result.data);
});

const initialTokens = storage.readObject('tokens') || {};
const tokens = createStore<Record<string, string>>(initialTokens)
  .on(getToken.done, (state, data) => {
    if (Object.prototype.hasOwnProperty.call(data.result, 'data')) {
      getCurrentAccount();
      return data.result?.data;
    } else {
      message.error('invalid response');
      return null;
    }
  })
  .on(saveTokens, (state, data) => data)
  .on(logout, () => ({}));

tokens.watch((tokens: Record<string, string>) => storage.writeObject('tokens', tokens));

const preloaders = createPreloadersStore([getToken, checkTotp, setupTotp]);

const initialUserState = (storage.readObject('user') as IAuthUser) || null;
const user = createStore<IUserState>(initialUserState)
  .on(logout, (state: IUserState) => {
    resetTrackers();
    return null;
  })
  .on(getCurrentAccount.done, (state: IUserState, data) => data.result?.data);
user.watch((user: IUserState) => storage.writeObject('user', user));

const auth = combine({ preloaders, user });

export default auth;
